import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle } from "lucide-react";

export default function InventoryReports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/inventory").then((res) => setR(res.data)).catch(() => {}); }, []);
  if (!r) return <div className="text-slate-400 text-sm">Loading…</div>;

  const stats = [
    ["Value at cost", money(r.store_value_cost)], ["Value at retail", money(r.store_value_retail)],
    ["Est. margin", money(r.margin)], ["Safe value (cost)", money(r.safe_value_cost)],
  ];

  return (
    <div className="max-w-6xl space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(([l, v]) => <Card key={l} className="p-4"><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-bold text-slate-900">{v}</div></Card>)}
      </div>

      {r.low_stock.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="low-stock-card">
          <div className="flex items-center gap-2 text-amber-700 font-medium mb-2"><AlertTriangle className="w-4 h-4" />Low stock alerts</div>
          <div className="flex flex-wrap gap-2">{r.low_stock.map((l, i) => <span key={i} className="text-xs bg-white border border-amber-200 rounded px-2 py-1">{l.name} · {Number(l.qty).toFixed(0)}</span>)}</div>
        </Card>
      )}

      <Tabs defaultValue="floor">
        <TabsList>
          <TabsTrigger value="floor" data-testid="tab-floor">Grove ST Floor</TabsTrigger>
          <TabsTrigger value="flower" data-testid="tab-flower-inv">Flower</TabsTrigger>
          <TabsTrigger value="safe" data-testid="tab-safe-inv">Safe</TabsTrigger>
        </TabsList>
        <TabsContent value="floor">
          <Card className="divide-y">
            {r.products.map((p) => (
              <div key={p.id} className="flex justify-between items-center p-3 text-sm"><span>{p.name} <span className="text-xs text-slate-400">· {p.category}</span></span>
                <span className="text-slate-500">{p.variants?.length ? p.variants.reduce((s, v) => s + (v.inventory_qty || 0), 0) : p.inventory_qty} on hand</span></div>
            ))}
            {r.products.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No products.</div>}
          </Card>
        </TabsContent>
        <TabsContent value="flower">
          <Card className="divide-y">
            {r.flower.map((f) => <div key={f.id} className="flex justify-between items-center p-3 text-sm"><span>{f.strain_name}</span><span className="text-slate-500">{(f.total_grams || 0).toFixed(1)}g</span></div>)}
            {r.flower.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No flower on floor.</div>}
          </Card>
        </TabsContent>
        <TabsContent value="safe">
          <Card className="divide-y">
            {r.safe.map((s) => <div key={s.id} className="flex justify-between items-center p-3 text-sm"><span>{s.name} <span className="text-xs text-slate-400">· {s.category}</span></span><span className="text-slate-500">{(s.total_grams || s.quantity || 0).toFixed(1)}</span></div>)}
            {r.safe.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Safe empty.</div>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
